import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { pdfjs } from "react-pdf";
import {
  alpha,
  Box,
  CircularProgress,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import {
  PDFData,
  RequestType,
  DocumentCreationSuccessTitle,
  defaultPinState,
} from "@interfaces";
import { DefaultWhiteText, StyledDocument, StyledPage } from "../styles";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutline";
import {
  Body,
  ColoredComponent,
  ExtraWhiteSpace,
  IconOptions,
  ShareDocumentsErrorModal,
  ContentContainer,
} from "@components";
import {
  BottomActionArea,
  BottomActionButton,
  SecondaryActionButton,
} from "@mekidoc/ui";
import {
  useGetPDFs,
  useIsScreenBiggerThanSM,
  useRequestData,
  useShareOrDownloadFiles,
  useClearAllInteractionsStores,
} from "@hooks";
import { handlePagesManagement } from "@utils";
import { useDocumentNotificationContext } from "@contexts/DocumentNotificationContext";
import { useDocumentSignatureContext } from "@contexts/index";
import { SidebarMenu } from "@screens/Requests/components";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ScreenWrapper = styled(Box)<{ padding?: string }>((props) => ({
  minHeight: "60vh",
  gap: "16px",
  ...(props.padding && {
    padding: props.padding,
  }),
}));

const CenteredWrapper = styled(ScreenWrapper)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  marginTop: "16px",
});

const ErrorContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const ErrorText = styled(Typography)({
  color: "#611a15",
  userSelect: "none",
});

const ErrorTextLink = styled("button")({
  border: "none",
  background: "none",
  fontSize: "16px",
  fontWeight: "500",
  lineHeight: "20px",
  letterSpacing: "0.5px",
  color: "#006bd6",
  textDecoration: "underline",
  cursor: "pointer",
  role: "button",
  tabIndex: 0,
  outline: "none",
  "&:focus": {
    outline: "2px solid #006bd6",
    outlineOffset: "2px",
    borderRadius: "2px",
  },
  "&:focus-visible": {
    outline: "2px solid #006bd6",
    outlineOffset: "2px",
    borderRadius: "2px",
  },
});

const Bold = styled("span")({
  fontWeight: 600,
});

const StyledBottomActionArea = styled(BottomActionArea)(({ theme }) => ({
  justifyContent: "center",
  position: "fixed",
  boxShadow: theme.shadows[2],
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
}));

const StyledSecondaryActionButton = styled(SecondaryActionButton)(
  ({ theme }) => ({
    maxWidth: "600px",
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  })
);

const StyledBottomActionButton = styled(BottomActionButton)(({ theme }) => ({
  maxWidth: "600px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

interface Props {
  requestType: RequestType;
}

export const DocumentCreationSuccess: React.FC<Props> = ({ requestType }) => {
  const theme = useTheme();
  const isScreenBiggerThanSM = useIsScreenBiggerThanSM();
  const {
    convertFiles,
    isConvertingFiles,
    shareOrDownloadFilesCallback,
    isDownloadFilesFallbackActive,
    setIsDownloadFilesFallbackActive,
  } = useShareOrDownloadFiles();
  const navigate = useNavigate();
  const { requestId = "" } = useParams();
  const location = useLocation();
  const { documentPDFData }: { documentPDFData?: PDFData[] } =
    location.state || {};

  const { data: request } = useRequestData();
  const {
    data: pdfData,
    isPending,
    isError,
  } = useGetPDFs({
    requestId,
    documentPDFData,
  });
  const [numPagesArray, setNumPagesArray] = useState<number[]>([]);
  useClearAllInteractionsStores();

  const {
    isErrorOnNotifyingPrescription,
    isNotifyingPrescription,
    notifyPrescription,
  } = useDocumentNotificationContext();

  const [notifyFailedRetriesCount, setNotifyFailedRetriesCount] = useState(0);
  const isNotifyRetriesExhausted = notifyFailedRetriesCount >= 1;

  // Use the document signature context
  const { setPinState } = useDocumentSignatureContext();

  const handleMainActionClick = useCallback(async () => {
    if (request && pdfData) {
      if (pdfData.treatment) {
        const pdfFiles = await convertFiles([pdfData.treatment]);
        await shareOrDownloadFilesCallback(pdfFiles, [pdfData.treatment]);
        return;
      }
      const pdfFiles = await convertFiles(pdfData.documents ?? []);
      await shareOrDownloadFilesCallback(pdfFiles, pdfData.documents ?? []);
    }
  }, [request, pdfData, convertFiles, shareOrDownloadFilesCallback]);

  const onGoHome = useCallback(() => {
    setPinState(defaultPinState);
    navigate("/app/solicitudes", {
      state: {
        documentCreated: true,
      },
    });
  }, [setPinState, navigate]);

  const onNotifyPrescriptionRetryClick = useCallback(async () => {
    try {
      await notifyPrescription({ requestId });
    } catch (error) {
      console.error("Error notifying prescription:", error);
      setNotifyFailedRetriesCount((prev) => prev + 1);
    }
  }, [notifyPrescription, requestId]);

  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      event.preventDefault();
      onGoHome();
    };
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handleBackButton);
    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [onGoHome]);

  if (isPending) {
    return (
      <>
        <SidebarMenu />
        <ContentContainer disableGutters>
          <Body>
            <CenteredWrapper>
              <CircularProgress size={24} color="primary" />
            </CenteredWrapper>
          </Body>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <SidebarMenu />
      <ContentContainer disableGutters>
        <Body>
          {isError ? (
            <ColoredComponent
              color={theme.palette.error.main}
              icon={IconOptions.ERROR}
              text={
                <DefaultWhiteText>
                  Error al obtener el documento.
                </DefaultWhiteText>
              }
            />
          ) : (
            <>
              {!isNotifyingPrescription && (
                <>
                  {isErrorOnNotifyingPrescription ? (
                    <ColoredComponent
                      color={alpha("#e2241a", 0.1)}
                      text={
                        <ErrorContainer>
                          <ErrorOutlinedIcon color="error" />
                          {isNotifyRetriesExhausted ? (
                            <ErrorText>
                              Lo sentimos, no se pudo enviar la receta, puedes
                              descargarla y enviarla manualmente al paciente.
                            </ErrorText>
                          ) : (
                            <ErrorText>
                              Ocurrió un problema al enviar el documento por
                              correo electrónico.{" "}
                              <ErrorTextLink
                                onClick={onNotifyPrescriptionRetryClick}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onNotifyPrescriptionRetryClick();
                                  }
                                }}
                              >
                                Volver a intentar
                              </ErrorTextLink>
                            </ErrorText>
                          )}
                        </ErrorContainer>
                      }
                    />
                  ) : (
                    <ColoredComponent
                      color={theme.palette.success.main}
                      icon={IconOptions.SUCCESS}
                      text={
                        <DefaultWhiteText>
                          {DocumentCreationSuccessTitle[requestType].document}{" "}
                          <Bold>
                            {DocumentCreationSuccessTitle[requestType].action}
                          </Bold>{" "}
                          por correo electrónico
                        </DefaultWhiteText>
                      }
                    />
                  )}
                </>
              )}

              <CenteredWrapper>
                {pdfData &&
                  (pdfData.treatment ? (
                    <StyledDocument
                      noData={
                        <Typography>No se encontró el documento</Typography>
                      }
                      file={pdfData.treatment.url}
                      loading={<CircularProgress size={24} color="primary" />}
                      onLoadSuccess={({ numPages }) => {
                        setNumPagesArray((prevNumPagesArray) =>
                          handlePagesManagement(prevNumPagesArray, 0, numPages)
                        );
                      }}
                    >
                      {numPagesArray[0] &&
                        Array.from(
                          new Array(numPagesArray[0]),
                          (_, pageIndex) => (
                            <StyledPage
                              key={pageIndex}
                              pageNumber={pageIndex + 1}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          )
                        )}
                    </StyledDocument>
                  ) : (
                    pdfData.documents.map(({ url }, index) => (
                      <StyledDocument
                        key={index}
                        noData={
                          <Typography>No se encontró el documento</Typography>
                        }
                        file={url}
                        loading={<CircularProgress size={24} color="primary" />}
                        onLoadSuccess={({ numPages }) => {
                          setNumPagesArray((prevNumPagesArray) =>
                            handlePagesManagement(
                              prevNumPagesArray,
                              index,
                              numPages
                            )
                          );
                        }}
                      >
                        {numPagesArray[index] &&
                          Array.from(
                            new Array(numPagesArray[index]),
                            (_, pageIndex) => (
                              <StyledPage
                                key={pageIndex}
                                pageNumber={pageIndex + 1}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                              />
                            )
                          )}
                      </StyledDocument>
                    ))
                  ))}
              </CenteredWrapper>
            </>
          )}

          <ShareDocumentsErrorModal
            isOpen={isDownloadFilesFallbackActive}
            onClose={() => setIsDownloadFilesFallbackActive(false)}
          />
          <ExtraWhiteSpace />
          <StyledBottomActionArea>
            <Box
              display="flex"
              gap="16px"
              justifyContent="center"
              maxWidth="600px"
              width="100%"
            >
              <StyledSecondaryActionButton
                useBackground
                onClick={handleMainActionClick}
                disabled={isConvertingFiles}
                startIcon={
                  isScreenBiggerThanSM ? (
                    <DownloadIcon
                      sx={(t) => ({ color: t.palette.primary.main })}
                    />
                  ) : (
                    <ShareIcon
                      sx={(t) => ({ color: t.palette.primary.main })}
                    />
                  )
                }
              >
                {isConvertingFiles ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isScreenBiggerThanSM ? (
                  "Descargar"
                ) : (
                  "Compartir"
                )}
              </StyledSecondaryActionButton>
              <StyledBottomActionButton onClick={onGoHome}>
                Ir al inicio
              </StyledBottomActionButton>
            </Box>
          </StyledBottomActionArea>
        </Body>
      </ContentContainer>
    </>
  );
};
